import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { Spinner } from '@/components/ui/Spinner';
import { aiApi } from '@/features/ai/api';
import { ApiRequestError } from '@/services/httpClient';

const TYPE_LABEL: Record<string, string> = { task: 'Task', project: 'Project', document: 'Document', file: 'File', lead: 'Lead', company: 'Company' };

export function AiSearchAction({ onBack, onNavigate }: { onBack: () => void; onNavigate: () => void }) {
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  const searchMutation = useMutation({
    mutationFn: () => aiApi.search(query.trim()),
    onError: (err: unknown) => setError(err instanceof ApiRequestError ? err.message : 'Search failed.'),
  });

  return (
    <div className="flex h-full flex-col">
      <button type="button" onClick={onBack} className="mb-3 flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to actions
      </button>

      <form onSubmit={(e) => { e.preventDefault(); if (query.trim()) searchMutation.mutate(); }} className="mb-3 flex gap-2">
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search tasks, projects, documents, files, CRM…" className="flex-1 rounded-xl border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100" />
        <Button type="submit" size="sm" isLoading={searchMutation.isPending} disabled={!query.trim()}><Search className="h-3.5 w-3.5" /></Button>
      </form>

      {error && <div className="mb-3"><Alert tone="error">{error}</Alert></div>}
      {searchMutation.isPending && <Spinner label="Searching…" />}

      {searchMutation.data && (
        <div className="flex-1 overflow-y-auto">
          {searchMutation.data.answer && (
            <div className="mb-3 flex items-start gap-2 rounded-xl border border-brand-100 bg-brand-50 p-3 dark:border-brand-900 dark:bg-brand-950/40">
              <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-600" />
              <p className="text-sm text-brand-800 dark:text-brand-300">{searchMutation.data.answer}</p>
            </div>
          )}
          <div className="flex flex-col divide-y divide-gray-100 dark:divide-gray-800">
            {searchMutation.data.results.map((r) => (
              <Link key={`${r.type}-${r.id}`} to={r.url} onClick={onNavigate} className="flex items-center gap-2 py-2 text-sm">
                <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400">{TYPE_LABEL[r.type] ?? r.type}</span>
                <span className="truncate text-gray-700 dark:text-gray-300">{r.title}</span>
              </Link>
            ))}
            {searchMutation.data.results.length === 0 && <p className="py-6 text-center text-sm text-gray-400">No matches found.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
